<template>
  <v-container fluid class="pa-0 pa-sm-3">
    <!-- Duplicate Dialog -->
    <v-dialog v-model="duplicateDialog" max-width="500px" persistent>
      <v-card v-if="currentRequest" class="pa-2">
        <v-card-title primary-title class="pb-0 mb-0">
          Create Duplicate Request
        </v-card-title>
        <v-card-text>
          <p>This will create a new truck requests and assign it to you.</p>
          <p>
            <strong>
              {{ currentRequest.jobSite.street }} ({{
                currentRequest.jobSite.jobId
              }})
            </strong>
          </p>
          <truck-request-details :showNotes="true" :order="currentRequest" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="duplicateDialog = false" class="mr-3"
            >cancel</v-btn
          >
          <v-btn color="blue" dark @click="createDuplicate"
            >Create Duplicate</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Arrival Confirmation Dialog -->
    <v-dialog v-model="arrivalDialog" max-width="400px" persistent>
      <v-card v-if="selectedItem">
        <v-card-title class="headline orange white--text">
          <v-icon left color="white">timer</v-icon>
          Confirm Arrival
        </v-card-title>
        <v-card-text class="pt-4">
          <p>Mark your arrival at:</p>
          <p class="font-weight-bold">
            {{ selectedItem.jobSite.street }}
            <br />
            <span class="caption">({{ selectedItem.jobSite.jobId }})</span>
          </p>
          <p class="caption grey--text">
            This will record your arrival time as {{ dialogTime }}.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="arrivalDialog = false">Cancel</v-btn>
          <v-btn color="orange" dark @click="confirmArrivalAction">
            <v-icon left small>timer</v-icon>
            Mark Arrived
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Finish Confirmation Dialog -->
    <v-dialog v-model="finishDialog" max-width="400px" persistent>
      <v-card v-if="selectedItem">
        <v-card-title class="headline green white--text">
          <v-icon left color="white">done</v-icon>
          Confirm Finish
        </v-card-title>
        <v-card-text class="pt-4">
          <p>Mark job as finished at:</p>
          <p class="font-weight-bold">
            {{ selectedItem.jobSite.street }}
            <br />
            <span class="caption">({{ selectedItem.jobSite.jobId }})</span>
          </p>
          <p class="caption grey--text">
            This will record your finish time as {{ dialogTime }}.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="finishDialog = false">Cancel</v-btn>
          <v-btn color="green" dark @click="confirmFinishAction">
            <v-icon left small>done</v-icon>
            Mark Finished
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Close Request Time Confirmation Dialog -->
    <v-dialog v-model="closeDialog" max-width="450px" persistent>
      <v-card v-if="selectedItem">
        <v-card-title class="headline primary white--text">
          <v-icon left color="white">verified</v-icon>
          Confirm Times to Close
        </v-card-title>
        <v-card-text class="pt-4">
          <p>Confirm the arrival and finish times for:</p>
          <p class="font-weight-bold">
            {{ selectedItem.jobSite.street }}
            <br />
            <span class="caption">({{ selectedItem.jobSite.jobId }})</span>
          </p>
          <p class="caption grey--text mb-4">
            Please verify or correct these times before closing.
          </p>
          <v-text-field
            v-model="closeArrivalTime"
            type="time"
            label="Arrival Time"
            outlined
            dense
            prepend-inner-icon="timer"
          ></v-text-field>
          <v-text-field
            v-model="closeFinishTime"
            type="time"
            label="Finish Time"
            outlined
            dense
            prepend-inner-icon="done"
          ></v-text-field>
          <v-alert v-if="closeError" type="error" dense text class="mb-0">
            {{ closeError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            dark
            :disabled="submittingClose"
            @click="confirmCloseAction"
          >
            Confirm &amp; Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Inspection Records (BIC: last 14 days, kept with the vehicle) -->
    <v-dialog v-model="recordsDialog" fullscreen scrollable>
      <truck-inspection-records
        v-if="recordsDialog"
        :truck="assignedTruck"
        @close="recordsDialog = false"
      />
    </v-dialog>

    <!-- Truck Change / Takeover Confirmation Dialog -->
    <v-dialog v-model="truckChangeDialog" max-width="440px" persistent>
      <v-card v-if="pendingTruck">
        <v-card-title class="headline">Confirm truck</v-card-title>
        <v-card-text class="pt-4">
          <p class="font-weight-bold">{{ pendingTruck.displayName }}</p>
          <v-alert
            v-if="pendingTruck.takenByOther"
            type="warning"
            text
            dense
            class="mb-2"
          >
            This truck is assigned to
            {{ pendingTruck.driver.firstName }}
            {{ pendingTruck.driver.lastName }}. Taking it will remove it from
            them.
          </v-alert>
          <v-alert
            v-if="inspection && !inspection.truckNotApplicable"
            type="info"
            text
            dense
            class="mb-0"
          >
            You will need to complete a new pre-trip inspection for this truck.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelTruckChange">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="assigningTruck"
            @click="confirmTruckChange"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Not Driving Today Confirmation Dialog -->
    <v-dialog v-model="notApplicableDialog" max-width="420px" persistent>
      <v-card>
        <v-card-title class="headline">Not driving today?</v-card-title>
        <v-card-text class="pt-4">
          <p>
            Only choose this if you are not driving a truck today. Your day will
            be recorded without a truck or pre-trip inspection.
          </p>
          <p class="caption grey--text mb-0">
            If you are driving, go back and pick your truck.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="notApplicableDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="submittingNotApplicable"
            @click="confirmNotApplicable"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row wrap class="justify-left">
      <v-col cols="12" xs="12" class="headline pb-0">My Schedule </v-col>

      <!-- Checking whether the day has been started -->
      <v-col cols="12" xs="12" v-if="checkingDay" class="text-center py-8">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-col>

      <!-- START OF DAY GATE -->
      <v-col cols="12" xs="12" v-else-if="gated">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left dark>local_shipping</v-icon>
            Start Your Day
          </v-card-title>
          <v-card-text class="pt-5 pb-0">
            Pick your truck and complete the pre-trip inspection before working
            your requests.
          </v-card-text>
          <v-stepper v-model="gateStep" vertical flat class="gate-stepper">
            <v-stepper-step :complete="!!assignedTruck" step="1">
              Select your truck
              <small v-if="assignedTruck">{{ assignedTruck.displayName }}</small>
            </v-stepper-step>
            <v-stepper-content step="1">
              <v-select
                :key="selectKey"
                :items="truckOptions"
                :value="assignedTruck"
                return-object
                item-text="label"
                hide-details
                label="Assigned truck"
                outlined
                @change="onTruckSelected"
              ></v-select>
              <v-switch
                v-model="showAssignedTrucks"
                label="Show trucks assigned to other drivers"
                hide-details
                dense
                class="mt-2 mb-4"
              ></v-switch>
              <v-btn
                color="primary"
                :disabled="!assignedTruck"
                @click="gateStep = 2"
              >
                Continue
              </v-btn>
              <v-btn text small class="ml-2" @click="notApplicableDialog = true">
                I'm not driving today
              </v-btn>
            </v-stepper-content>

            <v-stepper-step step="2">Pre-trip inspection</v-stepper-step>
            <v-stepper-content step="2">
              <pre-trip-inspection
                :truck="assignedTruck"
                @complete="onInspectionComplete"
              />
              <v-btn text small @click="gateStep = 1">Back</v-btn>
            </v-stepper-content>
          </v-stepper>
        </v-card>
      </v-col>

      <template v-else>
        <v-col cols="12" xs="12" v-if="gateFailOpen">
          <v-alert type="warning" outlined text class="mb-0">
            <strong>Pre-trip inspection unavailable.</strong>
            You have been let through so you can work your requests, but the
            inspection was not recorded. Please tell the office.
            <div class="caption mt-1">{{ gateError }}</div>
          </v-alert>
        </v-col>
        <v-col cols="12" xs="12" v-if="user.role == 'Driver'">
          <!-- Truck picker stays out of the way until they ask for it, so the
               day's truck can't be changed by a stray tap. -->
          <div v-if="changingTruck">
            <v-select
              :key="selectKey"
              :items="truckOptions"
              :value="assignedTruck"
              return-object
              item-text="label"
              hide-details
              label="Assigned truck"
              outlined
              @change="onTruckSelected"
            ></v-select>
            <v-switch
              v-model="showAssignedTrucks"
              label="Show trucks assigned to other drivers"
              hide-details
              dense
              class="mt-2"
            ></v-switch>
            <v-btn small text class="mt-2" @click="cancelChangeTruck">
              Cancel
            </v-btn>
          </div>
          <div v-else>
            <div class="d-flex align-center flex-wrap">
              <div class="mr-4">
                <span class="caption grey--text">Truck</span>
                <span class="font-weight-medium ml-1">
                  {{ assignedTruck ? assignedTruck.displayName : "—" }}
                </span>
              </div>
              <v-chip
                v-if="inspection"
                small
                :color="inspection.hasDefects ? 'warning' : 'success'"
                text-color="white"
              >
                <v-icon left small>
                  {{ inspection.hasDefects ? "warning" : "check_circle" }}
                </v-icon>
                <span v-if="inspection.truckNotApplicable">
                  Marked not driving today
                </span>
                <span v-else-if="inspection.hasDefects">
                  Pre-trip submitted with
                  {{ inspection.defects.length }} defective
                  {{ inspection.defects.length === 1 ? "item" : "items" }}
                </span>
                <span v-else>Pre-trip inspection complete</span>
              </v-chip>
            </div>
            <div class="mt-5">
              <v-btn
                small
                outlined
                color="grey darken-1"
                class="mr-2 mb-2"
                @click="changingTruck = true"
              >
                Change truck
              </v-btn>
              <v-btn
                v-if="assignedTruck"
                small
                outlined
                color="primary"
                class="mb-2"
                @click="recordsDialog = true"
              >
                <v-icon left small>description</v-icon>
                Inspection records
              </v-btn>
            </div>
          </div>
        </v-col>
        <v-col
          cols="12"
          xs="12"
          v-if="user.role == 'Driver' && dumpTicketsMissing.length"
        >
          <v-alert outlined type="info" text class="mb-0">
            <strong class="mr-2">DUMP TICKETS</strong> <br />
            {{ dumpTicketsMissing.length }}
            <span v-if="dumpTicketsMissing.length == 1">request</span
            ><span v-else>requests</span> missing dump tickets.
          </v-alert>
        </v-col>
        <!--  TRUCK REQUESTS -->
        <v-col cols="12" xs="12">
          <v-card>
            <v-tabs>
              <v-tab @click="type = 'scheduled'">
                Scheduled
                <span class="caption ml-1 grey--text">({{ scheduleCount }})</span>
              </v-tab>
              <v-tab @click="type = 'closed'">Closed</v-tab>
              <v-tab @click="type = 'cancelled'">Cancelled</v-tab>
            </v-tabs>
            <v-divider></v-divider>
            <v-data-table
              :items="currentTruckRequests"
              :headers="headers"
              disable-sort
            >
              <template v-slot:item.arrivalTime="{ item }">
                <div
                  v-if="item.status == 'scheduled'"
                  class="d-flex flex-column flex-sm-row align-start align-sm-center"
                >
                  <!-- Arrived Button -->
                  <v-btn
                    :disabled="!!item.arrivalTime"
                    :color="item.arrivalTime ? 'grey' : 'orange'"
                    :dark="!item.arrivalTime"
                    small
                    class="mb-2 mb-sm-0 mr-sm-2"
                    @click="confirmArrival(item)"
                  >
                    <v-icon left small>{{
                      item.arrivalTime ? "check" : "timer"
                    }}</v-icon>
                    {{ item.arrivalTime ? "Arrived" : "Mark Arrived" }}
                  </v-btn>

                  <!-- Finished Button -->
                  <v-btn
                    :disabled="!item.arrivalTime || !!item.finishTime"
                    :color="
                      item.finishTime
                        ? 'grey'
                        : item.arrivalTime
                        ? 'green'
                        : 'grey'
                    "
                    :dark="item.arrivalTime && !item.finishTime"
                    small
                    @click="confirmFinish(item)"
                  >
                    <v-icon left small>{{
                      item.finishTime ? "check" : "done"
                    }}</v-icon>
                    {{ item.finishTime ? "Finished" : "Mark Finished" }}
                  </v-btn>
                </div>

                <!-- Time Display -->
                <div
                  v-if="item.arrivalTime || item.finishTime"
                  class="mt-1 caption"
                >
                  <span v-if="item.arrivalTime">
                    Arrived: {{ item.arrivalTime.toDate() | moment("h:mma") }}
                  </span>
                  <span v-if="item.finishTime" class="ml-2">
                    Finished: {{ item.finishTime.toDate() | moment("h:mma") }}
                  </span>
                </div>
              </template>
              <template v-slot:item.address="{ item }">
                {{ item.jobSite.street }}
                <div class="caption">({{ item.jobSite.jobId }})</div>
              </template>
              <template v-slot:item.scheduledTime="{ item }">
                {{ item.scheduledTime }}
                <div class="caption">
                  {{ item.scheduledDay && item.scheduledDay.displayDate }}
                </div>
              </template>
              <template v-slot:item.requestType="{ item }">
                <truck-request-details :showNotes="true" :order="item" />
              </template>
              <template v-slot:item.orderedBy="{ item }">
                {{ item.orderedBy.firstName }}
                {{ item.orderedBy.lastName }}
              </template>
              <template v-slot:item.status="{ item }">
                <v-btn
                  v-if="item.status == 'scheduled'"
                  color="green"
                  dark
                  small
                  class="mr-4 mb-2 mt-6 mt-sm-0"
                  :href="`tel:${item.orderedBy.phone}`"
                  ><v-icon x-small left>phone</v-icon> Call
                  {{ item.orderedBy.firstName }}</v-btn
                >
                <v-btn
                  v-if="item.status == 'scheduled'"
                  color="primary"
                  small
                  class="mb-2 mt-6 mt-sm-0"
                  @click="confirmClose(item)"
                  >close</v-btn
                >
                <v-btn
                  v-if="item.status == 'closed'"
                  color="blue"
                  class="mr-2"
                  dark
                  small
                  @click="duplicate(item)"
                  >duplicate</v-btn
                >
                <v-btn
                  v-if="item.status == 'closed'"
                  color="primary"
                  small
                  :to="`/truck-requests/manage/orders/${item.orderNumber}`"
                  >view</v-btn
                >
              </template>
              <template v-slot:item.dumpTickets="{ item }">
                <v-icon v-if="item.dumpTickets" color="success"
                  >check_circle</v-icon
                >
                <v-icon v-else color="warning">help_outline</v-icon>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </template>
    </v-row>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <v-btn text @click="snack = false">Close</v-btn>
    </v-snackbar>
  </v-container>
</template>
<script>
import fb from "@/main";
import { mapState } from "vuex";
import utils from "@/helpers/utils";
import { sortBy } from "lodash";
import moment from "moment";
import truckRequestDetails from "@/components/truckRequests/truckRequestDetails";
import preTripInspection from "@/components/truckRequests/preTripInspection";
import truckInspectionRecords from "@/components/truckRequests/truckInspectionRecords";
export default {
  components: {
    truckRequestDetails,
    preTripInspection,
    truckInspectionRecords,
  },
  data() {
    return {
      type: "scheduled",
      dumpTicketsMissing: [],
      truckSchedule: [],
      trucksLoaded: false,
      showAssignedTrucks: false,
      changingTruck: false,
      recordsDialog: false,
      truckChangeDialog: false,
      pendingTruck: null,
      assigningTruck: false,
      selectKey: 0,
      inspection: null,
      inspectionLoaded: false,
      gateFailOpen: false,
      gateError: "",
      gateStep: 1,
      notApplicableDialog: false,
      submittingNotApplicable: false,
      snack: false,
      snackColor: "",
      snackText: "",
      duplicateDialog: false,
      arrivalDialog: false,
      finishDialog: false,
      closeDialog: false,
      closeArrivalTime: "",
      closeFinishTime: "",
      closeError: "",
      submittingClose: false,
      currentRequest: null,
      selectedItem: null,
      dialogTime: "",
      trucks: [],
      headers: [
        { text: "#", value: "orderNumber", width: 80 },
        { text: "Job", value: "address", width: 200 },
        { text: "Time", value: "scheduledTime", width: 180 },
        { text: "Details", value: "requestType", width: 200 },
        { text: "Requested By", value: "orderedBy" },
        { text: "Dispatcher Notes", value: "truckManagerNotes" },
        {
          text: "Dumped",
          value: "dumpTickets",
        },
        {
          text: "Arrival/Finish",
          value: "arrivalTime",
        },
        {
          text: "",
          value: "status",
        },
      ],
    };
  },
  computed: {
    ...mapState(["user", "jobSites"]),
    currentTruckRequests: function() {
      if (this.type == "closed") {
        return sortBy(
          this.truckSchedule.filter((tr) => tr.status == this.type),
          "closeDate",
        ).reverse();
      } else {
        return this.truckSchedule.filter((tr) => tr.status == this.type);
      }
    },
    scheduleCount: function() {
      return this.truckSchedule.filter((tr) => tr.status == "scheduled").length;
    },
    // Unassigned trucks by default. Trucks held by another driver are hidden
    // behind the toggle so they can't be taken by accident, but stay reachable
    // for a legitimate mid-day handoff.
    truckOptions: function() {
      const isMine = (t) => t.driver && t.driver.uid == this.user.uid;
      return this.trucks
        .filter((t) => t.active)
        .filter((t) => this.showAssignedTrucks || !t.driver || isMine(t))
        .map((t) => {
          const takenByOther = !!(t.driver && !isMine(t));
          const displayName = utils.truckName(t);
          return {
            ...t,
            takenByOther,
            displayName,
            label: takenByOther
              ? `${displayName} — ${t.driver.firstName} ${t.driver.lastName}`
              : displayName,
          };
        })
        .sort((a, b) => {
          if (a.takenByOther !== b.takenByOther) return a.takenByOther ? 1 : -1;
          return a.displayName.localeCompare(b.displayName, undefined, {
            numeric: true,
          });
        });
    },
    assignedTruck: function() {
      return (
        this.truckOptions.find(
          (t) => t.driver && t.driver.uid == this.user.uid,
        ) || null
      );
    },
    checkingDay: function() {
      return (
        this.user.role == "Driver" && !this.inspectionLoaded && !this.gateFailOpen
      );
    },
    // Drivers must pick a truck and submit a pre-trip inspection before they can
    // work their requests. Fails open so a driver is never locked out.
    gated: function() {
      if (this.user.role != "Driver") return false;
      if (this.gateFailOpen) return false;
      if (!this.inspectionLoaded) return false;
      return !this.inspection;
    },
  },
  async created() {
    this.unsubscribeTruckSchedule = await fb.db
      .collection("truckRequests")
      .where("scheduledDriver.uid", "==", this.user.uid)
      .orderBy("orderDate", "desc")
      .limit(100)
      .onSnapshot((querySnapshot) => {
        var truckSchedule = [];
        querySnapshot.forEach(function(doc) {
          truckSchedule.push({
            ...doc.data(),
          });
        });
        // console.log(truckSchedule);
        this.truckSchedule = truckSchedule;
      });
    // Live so the assigned/unassigned state of each truck stays current while a
    // driver sits on this page deciding.
    this.unsubscribeTrucks = await fb.db
      .collection("trucks")
      .onSnapshot((querySnapshot) => {
        const trucks = [];
        querySnapshot.forEach((doc) => {
          trucks.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        this.trucks = trucks;
        if (!this.trucksLoaded) {
          this.trucksLoaded = true;
          this.loadInspection();
        }
      });
    this.dumpTicketsMissing = this.truckSchedule.filter(
      (tr) => tr.status == "closed" && !tr.dumpTickets,
    );
  },
  watch: {
    // Changing trucks means the inspection on file no longer covers what they
    // are driving, so re-check and let the gate reopen if it doesn't.
    assignedTruck: function(newTruck, oldTruck) {
      const newId = newTruck ? newTruck.id : null;
      const oldId = oldTruck ? oldTruck.id : null;
      if (newId === oldId || !this.trucksLoaded) return;
      this.loadInspection();
    },
  },
  beforeDestroy() {
    this.unsubscribeTruckSchedule();
    if (this.unsubscribeTrucks) this.unsubscribeTrucks();
  },
  methods: {
    truckRequestType(type) {
      this.type = type;
    },

    onTruckSelected(truck) {
      if (!truck) return;
      if (this.assignedTruck && this.assignedTruck.id == truck.id) return;
      const needsInspection = !!(
        this.inspection && !this.inspection.truckNotApplicable
      );
      if (truck.takenByOther || needsInspection) {
        this.pendingTruck = truck;
        this.truckChangeDialog = true;
        return;
      }
      this.assignTruck(truck);
    },
    cancelTruckChange() {
      this.truckChangeDialog = false;
      this.pendingTruck = null;
      // force the select back to the truck they actually hold
      this.selectKey += 1;
    },
    cancelChangeTruck() {
      this.changingTruck = false;
      this.showAssignedTrucks = false;
      this.selectKey += 1;
    },
    async confirmTruckChange() {
      await this.assignTruck(this.pendingTruck);
    },
    async assignTruck(truck) {
      this.assigningTruck = true;
      try {
        await utils.assignDriver(this.user, truck.id);
        this.truckChangeDialog = false;
        this.pendingTruck = null;
        this.changingTruck = false;
        this.showAssignedTrucks = false;
      } catch (error) {
        console.log("Error assigning truck:", error);
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "Error assigning truck. Please try again.";
        this.selectKey += 1;
      }
      this.assigningTruck = false;
    },
    inspectionId(truckId) {
      const date = moment().format("YYYY-MM-DD");
      return `${this.user.uid}_${date}_${truckId || "none"}`;
    },
    async loadInspection() {
      if (this.user.role != "Driver") {
        this.inspectionLoaded = true;
        return;
      }
      try {
        const truckId = this.assignedTruck ? this.assignedTruck.id : null;
        const doc = await fb.db
          .collection("preTripInspections")
          .doc(this.inspectionId(truckId))
          .get();
        this.inspection = doc.exists ? doc.data() : null;
        this.gateStep = this.assignedTruck ? 2 : 1;
      } catch (error) {
        // never leave a driver unable to act on their requests
        console.error(
          "PRE-TRIP GATE BYPASSED - could not read preTripInspections:",
          error,
        );
        this.gateError = error.message || "Unknown error";
        this.gateFailOpen = true;
      }
      this.inspectionLoaded = true;
    },
    async onInspectionComplete() {
      await this.loadInspection();
      this.snack = true;
      this.snackColor = "success";
      this.snackText = "Pre-trip inspection submitted!";
    },
    async confirmNotApplicable() {
      this.submittingNotApplicable = true;
      try {
        // release any truck they picked so the day is recorded as truckless
        if (this.assignedTruck) {
          await fb.db
            .collection("trucks")
            .doc(this.assignedTruck.id)
            .update({ driver: null });
        }
        await fb.db
          .collection("preTripInspections")
          .doc(this.inspectionId(null))
          .set({
            date: moment().format("YYYY-MM-DD"),
            driver: {
              uid: this.user.uid,
              firstName: this.user.firstName,
              lastName: this.user.lastName,
              email: this.user.email,
              phone: this.user.phone || null,
            },
            truck: null,
            truckNotApplicable: true,
            items: [],
            defects: [],
            hasDefects: false,
            remarks: null,
            satisfactory: false,
            complete: true,
            createDate: fb.timestamp,
          });
        this.notApplicableDialog = false;
        this.submittingNotApplicable = false;
        await this.loadInspection();
      } catch (error) {
        console.log("Error recording not-driving status:", error);
        this.submittingNotApplicable = false;
        this.notApplicableDialog = false;
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "Error saving. Please try again.";
      }
    },
    confirmArrival(item) {
      this.selectedItem = item;
      this.dialogTime = moment().format("h:mma");
      this.arrivalDialog = true;
    },
    confirmFinish(item) {
      this.selectedItem = item;
      this.dialogTime = moment().format("h:mma");
      this.finishDialog = true;
    },
    confirmClose(item) {
      this.selectedItem = item;
      this.closeArrivalTime = item.arrivalTime
        ? moment(item.arrivalTime.toDate()).format("HH:mm")
        : "";
      this.closeFinishTime = item.finishTime
        ? moment(item.finishTime.toDate()).format("HH:mm")
        : "";
      this.closeError = "";
      this.closeDialog = true;
    },
    mergeTimeOfDay(existingTimestamp, hhmm) {
      const [hours, minutes] = hhmm.split(":").map(Number);
      const base = existingTimestamp ? existingTimestamp.toDate() : new Date();
      const result = new Date(base);
      result.setHours(hours, minutes, 0, 0);
      return result;
    },
    async confirmCloseAction() {
      this.closeError = "";
      if (!this.closeArrivalTime || !this.closeFinishTime) {
        this.closeError = "Both arrival and finish times are required.";
        return;
      }
      const arrivalDate = this.mergeTimeOfDay(
        this.selectedItem.arrivalTime,
        this.closeArrivalTime,
      );
      const finishDate = this.mergeTimeOfDay(
        this.selectedItem.finishTime,
        this.closeFinishTime,
      );
      const now = new Date();
      if (arrivalDate > now || finishDate > now) {
        this.closeError = "Times cannot be in the future.";
        return;
      }
      if (finishDate < arrivalDate) {
        this.closeError = "Finish time must be after arrival time.";
        return;
      }
      const diffHours = (finishDate - arrivalDate) / (1000 * 60 * 60);
      if (diffHours > 12) {
        this.closeError =
          "Finish time cannot be more than 12 hours after arrival.";
        return;
      }
      this.submittingClose = true;
      try {
        const orderNumber = this.selectedItem.orderNumber;
        await fb.truckRequests_DB.doc(orderNumber).update({
          arrivalTime: arrivalDate,
          finishTime: finishDate,
        });
        this.closeDialog = false;
        this.selectedItem = null;
        this.submittingClose = false;
        this.$router.push(`/truck-requests/manage/orders/${orderNumber}`);
      } catch (error) {
        console.log("Error confirming close times:", error);
        this.submittingClose = false;
        this.closeError = "Error saving times. Please try again.";
      }
    },
    async confirmArrivalAction() {
      try {
        await fb.truckRequests_DB
          .doc(this.selectedItem.orderNumber)
          .update({ arrivalTime: new Date() });
        this.arrivalDialog = false;
        this.selectedItem = null;
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Arrival time recorded!";
      } catch (error) {
        console.log("Error setting arrival time:", error);
        this.arrivalDialog = false;
        this.selectedItem = null;
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "Error setting arrival time!";
      }
    },
    async confirmFinishAction() {
      try {
        const currentTime = new Date();
        const arrivalTime = this.selectedItem.arrivalTime.toDate();
        const diffInHours = (currentTime - arrivalTime) / (1000 * 60 * 60);

        if (diffInHours > 12) {
          this.finishDialog = false;
          this.selectedItem = null;
          this.snack = true;
          this.snackColor = "error";
          this.snackText =
            "Cannot set finish time. Time since arrival exceeds 12 hours.";
          return;
        }

        await fb.truckRequests_DB
          .doc(this.selectedItem.orderNumber)
          .update({ finishTime: currentTime });

        this.finishDialog = false;
        this.selectedItem = null;
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Finish time recorded!";
      } catch (error) {
        console.log("Error setting finish time:", error);
        this.finishDialog = false;
        this.selectedItem = null;
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "Error setting finish time!";
      }
    },
    async setArrivalTime(id) {
      await fb.truckRequests_DB.doc(id).update({ arrivalTime: new Date() });
      this.snack = true;
      this.snackColor = "success";
      this.snackText = "Set arrival time!";
    },
    async setFinishTime(item) {
      try {
        const currentTime = new Date();
        const arrivalTime = item.arrivalTime.toDate();
        const diffInHours = (currentTime - arrivalTime) / (1000 * 60 * 60);

        if (diffInHours > 12) {
          this.snack = true;
          this.snackColor = "error";
          this.snackText =
            "Cannot set finish time. Time since arrival exceeds 12 hours.";
          return;
        }

        await fb.truckRequests_DB
          .doc(item.orderNumber)
          .update({ finishTime: currentTime });
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Set finish time!";
      } catch (error) {
        console.log("Error setting finish time:", error);
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "Error setting finish time!";
      }
    },
    async duplicate(request) {
      this.currentRequest = request;
      this.duplicateDialog = true;
    },
    async createDuplicate() {
      const dupe = { ...this.currentRequest };
      try {
        await this.$store.dispatch("duplicateTruckRequest", { fb, dupe });
        this.duplicateDialog = false;
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Duplicated request!";
      } catch (error) {
        console.log(error);
        this.duplicateDialog = false;
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "error duplicating request.";
      }
    },
  },
};
</script>

<style>
/* Reclaim the width Vuetify reserves for the vertical stepper's rail.
   Drivers are on phones, so the inspection form needs the full card.
   The .v-application--is-* prefix is needed to outweigh Vuetify's own
   .v-application--is-ltr .v-stepper--vertical .v-stepper__content rule. */
.v-application--is-ltr .gate-stepper.v-stepper--vertical .v-stepper__content,
.v-application--is-rtl .gate-stepper.v-stepper--vertical .v-stepper__content {
  margin-left: 0;
  margin-right: 0;
  padding-left: 12px;
  padding-right: 12px;
  border-left: none;
}
.gate-stepper.v-stepper--vertical .v-stepper__step {
  padding-left: 16px;
}
</style>
